import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import type { ScoreHistory } from '@sdd-maturity/shared-types';
import type { DimensionName } from '@sdd-maturity/shared-types';
import { formatDimension } from '../lib/rubrics';

export interface ScoreHistoryChartProps {
  data: ScoreHistory[];
  /** When true, renders per-dimension trend lines in addition to overall */
  showDimensions?: boolean;
}

// Bound to the shared --genai-sem-chart-series-* tokens (tokens.css light, theme.css
// dark) so the dimension lines track the Interstate brand palette, brighten one step in
// dark mode, and stay consistent with the feature-page chart (FeatureDrillDown) — rather
// than each page keeping a standalone Tailwind-default palette. series-N maps to the Nth
// entry in ALL_DIMENSIONS below.
const DIMENSION_COLORS: Record<DimensionName, string> = {
  'constitution-maturity': 'var(--genai-sem-chart-series-1)',
  'artifact-traceability': 'var(--genai-sem-chart-series-2)',
  'supplemental-context': 'var(--genai-sem-chart-series-3)',
  'feedback-loop': 'var(--genai-sem-chart-series-4)',
  'ai-collaboration': 'var(--genai-sem-chart-series-5)',
  'framework-detection': 'var(--genai-sem-chart-series-6)',
};

function formatDate(ts: string): string {
  const d = new Date(ts);
  return `${(d.getMonth() + 1).toString()}/${d.getDate().toString()}`;
}

interface ChartDataPoint {
  date: string;
  overall: number;
  [key: string]: string | number;
}

// Score-persister writes -1 as a sentinel for repos the KTLO classifier marked
// maintenance-only ("not scored, no SDD-relevant work this period"). Drop those
// entries entirely so the X-axis doesn't allocate slots for skipped scans —
// otherwise the trend renders large empty gaps for KTLO periods.
function toChartData(data: ScoreHistory[], showDimensions: boolean): ChartDataPoint[] {
  return data
    .filter((entry) => entry.overallScore >= 0)
    .map((entry) => {
      const point: ChartDataPoint = {
        date: formatDate(entry.scanTimestamp),
        overall: entry.overallScore,
      };
      if (showDimensions) {
        for (const dim of entry.dimensions) {
          if (dim.score >= 0) point[dim.dimension] = dim.score;
        }
      }
      return point;
    });
}

const ALL_DIMENSIONS: DimensionName[] = [
  'constitution-maturity',
  'artifact-traceability',
  'supplemental-context',
  'feedback-loop',
  'ai-collaboration',
  'framework-detection',
];

export function ScoreHistoryChart({
  data,
  showDimensions = false,
}: ScoreHistoryChartProps) {
  if (data.length === 0) {
    return <p className="text-muted">No score history available.</p>;
  }

  const chartData = toChartData(data, showDimensions);

  return (
    <div data-testid="score-history-chart">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
          <Tooltip />
          {showDimensions && <Legend />}
          <Line
            type="monotone"
            dataKey="overall"
            stroke="var(--genai-sem-accent, #117d9b)"
            strokeWidth={3}
            dot={{ r: 3 }}
            name="Overall"
          />
          {showDimensions &&
            ALL_DIMENSIONS.map((dim) => (
              <Line
                key={dim}
                type="monotone"
                dataKey={dim}
                stroke={DIMENSION_COLORS[dim]}
                strokeWidth={2}
                dot={false}
                name={formatDimension(dim)}
              />
            ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
